import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { SessionService } from "@kno2/core/session";
import { UserIdProofProcessStatus } from "@kno2/core/session/user-profile.model";
import { LocalStorageService, SessionStorageService } from "@kno2/core/storage";
import { DialogService } from "@kno2/shared/dialog";
import {
    OnboardingIdProofingConfirmationModalComponent,
    OnboardingIdProofingManualModalComponent,
    OnboardingIdProofingStartModalComponent
} from "./id-proofing";
import { OnboardingModalHeaderComponent } from "./onboarding-modal-header.component";
import { OnboardingOrgActivationDelayModalComponent } from "./onboarding-org-activation-delay-modal.component";
import { OnboardingTermsAndConditionsModalComponent, OnboardingTermsAndConditionsResellersModalComponent } from "./terms-and-conditions";

@Injectable({
    providedIn: "root"
})
export class OnboardingOrchestratorService {
    private readonly sessionService = inject(SessionService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly dialogService = inject(DialogService);
    private readonly router = inject(Router);

    public async initializeIdentityProofingWorkflow(): Promise<void> {
        if (this.router.url.startsWith("/oauth/callback/")) {
            return;
        }

        const profile = this.sessionService.profileData();

        if (!profile?.isAdmin) {
            return;
        }

        let action: (() => Promise<void>) | null = null;

        if (!profile.orgIsAcceptedEULA) {
            action = profile.resellerBypassTos ? async () => await this.openResellerTosModal() : async () => await this.openTermsAndConditionsModal();
        } else if (
            !profile.orgIsIdProofed &&
            (profile.idProofProcessStatus === UserIdProofProcessStatus.NotIdProofed || profile.idProofProcessStatus === UserIdProofProcessStatus.Started)
        ) {
            action = async () => await this.openIdentityProofingStartModal();
        } else if (!profile.orgIsIdProofed && profile.idProofProcessStatus === UserIdProofProcessStatus.AwaitingUserClearDataConfirmation) {
            action = async () => await this.openIdProofingConfirmationModal();
        } else if (!profile.orgIsIdProofed && profile.idProofProcessStatus === UserIdProofProcessStatus.AwaitingProofUpload) {
            action = async () => await this.openManualVerificationModal();
        } else if (
            (profile.orgIsIdProofed || profile.idProofProcessStatus === UserIdProofProcessStatus.AwaitingManualProofing) &&
            this.shouldDisplayOrgActivationDelayModal()
        ) {
            action = async () => await this.openOrgActivationDelayModal();
        }

        if (action) {
            setTimeout(action, 1000);
        }
    }

    private async openTermsAndConditionsModal(): Promise<void> {
        await this.dialogService.open(OnboardingTermsAndConditionsModalComponent, {
            dialogId: "terms-and-conditions-modal",
            closeOnEscape: false,
            headerText: "License Agreement",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private async openResellerTosModal(): Promise<void> {
        await this.dialogService.open(OnboardingTermsAndConditionsResellersModalComponent, {
            dialogId: "reseller-terms-and-conditions-modal",
            closeOnEscape: false,
            headerText: "License Agreement",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private async openIdentityProofingStartModal(): Promise<void> {
        await this.dialogService.open(OnboardingIdProofingStartModalComponent, {
            dialogId: "id-proofing-start-modal",
            closeOnEscape: false,
            headerText: "Identity Verification",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private async openIdProofingConfirmationModal(): Promise<void> {
        await this.dialogService.open(OnboardingIdProofingConfirmationModalComponent, {
            dialogId: "id-proofing-confirmation-modal",
            closeOnEscape: false,
            headerText: "Review and Confirm Your Information",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private async openManualVerificationModal(): Promise<void> {
        await this.dialogService.open(OnboardingIdProofingManualModalComponent, {
            dialogId: "id-proofing-manual-modal",
            closeOnEscape: false,
            headerText: "Manual Verification",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private async openOrgActivationDelayModal(): Promise<void> {
        await this.dialogService.open(OnboardingOrgActivationDelayModalComponent, {
            dialogId: "org-activation-delay-modal",
            headerText: "Welcome!",
            templates: {
                header: OnboardingModalHeaderComponent
            }
        });
    }

    private shouldDisplayOrgActivationDelayModal(): boolean {
        const profile = this.sessionService.profileData();
        const hideOrgActivationDelayModal =
            this.localStorageService.get<boolean>("hideOrgActivationDelayModal") && this.sessionStorageService.get<boolean>("hideOrgActivationDelayModal");

        return !profile?.orgIsActivated && hideOrgActivationDelayModal !== true;
    }
}
